import React, { useState } from "react";
import { 
  Shield, Fuel, Wrench, Monitor, Leaf, TrendingUp,
  ChevronRight, ArrowLeft, Check, Phone, Mail, MapPin,
  Play, Users, Target, Zap
} from "lucide-react";

// Logo MOBILSAFE estilo web principal (verde esmeralda)
const MOBILSAFE_LOGO = "/logo-mobilsafe.png";

// Componente de líneas diagonales decorativas (estilo mobilsafe.es)
const DiagonalLines = ({ className = "" }) => (
  <div className={`absolute pointer-events-none opacity-10 ${className}`}>
    <svg width="200" height="200" viewBox="0 0 200 200">
      <pattern id="diagonals" patternUnits="userSpaceOnUse" width="20" height="20">
        <line x1="0" y1="20" x2="20" y2="0" stroke="#10b981" strokeWidth="1"/>
      </pattern>
      <rect width="200" height="200" fill="url(#diagonals)"/>
    </svg>
  </div>
);

const MockupFondoBlancoV2 = () => {
  const [vista, setVista] = useState('selector');

  // Selector de mockups
  if (vista === 'selector') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-8">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full mb-6">
              <span className="text-emerald-400 text-sm font-medium">VERSIÓN 2 - Estilo mobilsafe.es</span>
            </div>
            <h1 className="text-3xl font-black text-white mb-4">MOCKUP V2 - Fondo Blanco</h1>
            <p className="text-slate-400">Basado en el estilo visual de www.mobilsafe.es</p>
            <p className="text-amber-400 text-sm mt-2">Simulación visual - No afecta al código de producción</p>
          </div>
          
          <div className="grid gap-6">
            <button
              onClick={() => setVista('landing')}
              className="p-6 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-2xl text-left transition-all group"
            >
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-emerald-400">Landing Principal (Simulador)</h3>
              <p className="text-slate-400">Estilo minimalista con verde esmeralda</p>
            </button>
            
            <button
              onClick={() => setVista('caes')}
              className="p-6 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-2xl text-left transition-all group"
            >
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-emerald-400">Landing CAES</h3>
              <p className="text-slate-400">Tu Ingreso CAES con diseño corporativo</p>
            </button>
          </div>
          
          <div className="mt-8 p-4 bg-slate-800 rounded-xl">
            <h4 className="text-white font-medium mb-2">Cambios aplicados en V2:</h4>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• Verde esmeralda como color principal (no naranja)</li>
              <li>• Más espacio blanco entre secciones</li>
              <li>• Tipografía más grande y contundente</li>
              <li>• Stats destacados estilo "+28%"</li>
              <li>• Footer minimalista con datos de contacto</li>
              <li>• Líneas diagonales decorativas</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Mockup Landing Principal V2
  if (vista === 'landing') {
    return (
      <div className="min-h-screen bg-white">
        {/* Header - Más espaciado, estilo mobilsafe.es */}
        <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-8 py-5 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img src={MOBILSAFE_LOGO} alt="MOBILSAFE" className="h-7" />
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#" className="text-slate-600 hover:text-emerald-600 font-medium text-sm">SOLUCIONES</a>
              <a href="#" className="text-slate-600 hover:text-emerald-600 font-medium text-sm">SOBRE NOSOTROS</a>
              <a href="#" className="text-slate-600 hover:text-emerald-600 font-medium text-sm">BLOG</a>
              <button className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-bold text-sm transition-colors">
                CONTACTO
              </button>
            </nav>
          </div>
        </header>

        {/* Hero - Más espacio, H1 más grande */}
        <section className="py-24 px-8 relative overflow-hidden">
          <DiagonalLines className="right-0 top-0" />
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-emerald-600 font-bold text-sm tracking-wider mb-6">
                  SIMULADOR DE IMPACTO SOSTENIBLE
                </p>
                <h1 className="text-5xl lg:text-6xl font-black text-slate-900 mb-8 leading-[1.1]">
                  REDUCE COSTES.<br/>
                  <span className="text-emerald-500">AUMENTA INGRESOS.</span>
                </h1>
                <p className="text-xl text-slate-600 mb-10 leading-relaxed max-w-lg">
                  Calcula en segundos el impacto de MOBILSAFE en tu flota. 
                  Ahorro de combustible, reducción de emisiones y certificados CAES monetizables.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-bold flex items-center justify-center gap-2 transition-all hover:shadow-lg">
                    CALCULA TU AHORRO <ChevronRight className="w-5 h-5" />
                  </button>
                  <button className="px-8 py-4 border-2 border-slate-300 hover:border-emerald-500 text-slate-700 hover:text-emerald-600 rounded-lg font-bold flex items-center justify-center gap-2 transition-all">
                    VER DEMO <Play className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Stats Cards - Estilo mobilsafe.es */}
              <div className="bg-slate-50 rounded-3xl p-10 relative">
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl"></div>
                <h3 className="text-lg font-bold text-slate-800 mb-8">¿POR QUÉ MOBILSAFE?</h3>
                <div className="space-y-6">
                  <div className="flex items-center gap-6">
                    <div className="text-5xl font-black text-emerald-500">28%</div>
                    <div>
                      <div className="font-bold text-slate-800">Productividad</div>
                      <div className="text-sm text-slate-500">Mejora en tiempos de respuesta</div>
                    </div>
                  </div>
                  <div className="h-px bg-slate-200"></div>
                  <div className="flex items-center gap-6">
                    <div className="text-5xl font-black text-emerald-500">15%</div>
                    <div>
                      <div className="font-bold text-slate-800">Beneficios</div>
                      <div className="text-sm text-slate-500">Incremento en rentabilidad</div>
                    </div>
                  </div>
                  <div className="h-px bg-slate-200"></div>
                  <div className="flex items-center gap-6">
                    <div className="text-5xl font-black text-emerald-500">32%</div>
                    <div>
                      <div className="font-bold text-slate-800">Gastos</div>
                      <div className="text-sm text-slate-500">Reducción en costes operativos</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Áreas de impacto */}
        <section className="py-20 px-8 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-black text-slate-900 mb-4">
                ÁREAS DE IMPACTO
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Selecciona un área para calcular su impacto específico en tu flota
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: Shield, name: "SEGURIDAD", desc: "Reduce accidentes y riesgo laboral", stat: "-45% incidentes" },
                { icon: Fuel, name: "COMBUSTIBLE", desc: "Optimiza consumo y reduce costes", stat: "-8% consumo" },
                { icon: Monitor, name: "OPERATIVA", desc: "Cumple plazos y elimina tiempos muertos", stat: "+28% eficiencia" },
                { icon: Wrench, name: "MANTENIMIENTO", desc: "Anticipa averías y prolonga vida útil", stat: "-20% costes" },
                { icon: TrendingUp, name: "TIC / DATOS", desc: "Integración sencilla con tus sistemas", stat: "100% compatible" },
                { icon: Leaf, name: "SOSTENIBILIDAD", desc: "Reduce CO₂ e ingresa dinero con CAES", stat: "+15K€/año" },
              ].map((area, i) => (
                <div 
                  key={i} 
                  className="p-8 bg-white border border-slate-200 rounded-2xl cursor-pointer transition-all hover:shadow-xl hover:border-emerald-300 group"
                >
                  <div className="w-14 h-14 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center mb-5 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                    <area.icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-bold text-slate-800 text-lg mb-2">{area.name}</h3>
                  <p className="text-slate-500 mb-4">{area.desc}</p>
                  <div className="text-emerald-600 font-bold text-sm">{area.stat}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Clientes - Carrusel de logos estilo mobilsafe.es */}
        <section className="py-16 px-8 border-y border-slate-100">
          <div className="max-w-7xl mx-auto">
            <p className="text-center text-slate-500 font-medium mb-10">
              ¡ESTOS CLIENTES YA CUENTAN CON MOBILSAFE!
            </p>
            <div className="flex flex-wrap justify-center items-center gap-12 opacity-60">
              {['COCA-COLA', 'CANON', 'FUJITSU', 'BP', 'PEPSICO', 'GOODYEAR'].map((client, i) => (
                <div key={i} className="text-xl font-black text-slate-400">{client}</div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-24 px-8 bg-emerald-600 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
          </div>
          <div className="max-w-3xl mx-auto text-center relative">
            <h2 className="text-4xl font-black text-white mb-6">
              ¿LISTO PARA OPTIMIZAR TU FLOTA?
            </h2>
            <p className="text-xl text-emerald-100 mb-10">
              Solicita tu demo gratuita y descubre todo lo que MOBILSAFE puede hacer por tu empresa.
            </p>
            <button className="px-10 py-5 bg-white hover:bg-slate-50 text-emerald-600 rounded-xl font-bold text-lg transition-all hover:shadow-xl">
              SOLICITA TU DEMO GRATIS
            </button>
          </div>
        </section>

        {/* Footer - Minimalista estilo mobilsafe.es */}
        <footer className="py-12 px-8 bg-white border-t border-slate-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-10 mb-10">
              <div>
                <img src={MOBILSAFE_LOGO} alt="MOBILSAFE" className="h-7 mb-4" />
                <p className="text-slate-500 text-sm">
                  Consultora e ingeniería de soluciones tecnológicas para la gestión de flotas.
                </p>
              </div>
              <div>
                <h4 className="font-bold text-slate-800 mb-4">Visítanos</h4>
                <p className="text-slate-500 text-sm flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 text-emerald-500" />
                  Paseo Mikeletegi 54, 12.<br/>20009 San Sebastián
                </p>
              </div>
              <div>
                <h4 className="font-bold text-slate-800 mb-4">Llámanos</h4>
                <p className="text-slate-500 text-sm flex items-center gap-2">
                  <Phone className="w-4 h-4 text-emerald-500" />
                  +34 663 837 350
                </p>
              </div>
              <div>
                <h4 className="font-bold text-slate-800 mb-4">Escríbenos</h4>
                <p className="text-slate-500 text-sm flex items-center gap-2">
                  <Mail className="w-4 h-4 text-emerald-500" />
                  info@mobilsafe.es
                </p>
              </div>
            </div>
            <div className="pt-8 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-slate-400 text-sm">© 2026 MOBILSAFE Fleet Technology</p>
              <div className="flex gap-6">
                <a href="#" className="text-slate-400 hover:text-emerald-600 text-sm">Política de privacidad</a>
                <a href="#" className="text-slate-400 hover:text-emerald-600 text-sm">Política de cookies</a>
                <a href="#" className="text-slate-400 hover:text-emerald-600 text-sm">Aviso legal</a>
              </div>
            </div>
          </div>
        </footer>

        {/* Botón volver */}
        <button
          onClick={() => setVista('selector')}
          className="fixed bottom-6 left-6 px-4 py-3 bg-slate-900 text-white rounded-full shadow-xl flex items-center gap-2 hover:bg-slate-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" /> Volver
        </button>
      </div>
    );
  }

  // Mockup Landing CAES V2
  if (vista === 'caes') {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-8 py-5 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img src={MOBILSAFE_LOGO} alt="MOBILSAFE" className="h-7" />
            </div>
            <button className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-bold text-sm transition-colors">
              CONTACTO
            </button>
          </div>
        </header>

        {/* Hero CAES - Estilo corporativo verde */}
        <section className="py-24 px-8 relative overflow-hidden">
          <DiagonalLines className="right-0 top-0" />
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-emerald-100 text-emerald-700 px-5 py-2 rounded-full text-sm font-bold mb-8">
              CERTIFICADOS DE AHORRO ENERGÉTICO
            </div>
            <h1 className="text-5xl lg:text-6xl font-black text-slate-900 mb-8 leading-[1.1]">
              Tu flota tiene<br/>
              <span className="text-emerald-500">dinero dormido</span>
            </h1>
            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              Descubre cuánto puedes ganar con los Certificados de Ahorro Energético (CAES) 
              gracias a la conducción eficiente de tu flota.
            </p>
            <button className="px-10 py-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold text-lg transition-all hover:shadow-xl flex items-center gap-3 mx-auto">
              CALCULAR MI INGRESO CAES <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </section>

        {/* Qué son los CAES */}
        <section className="py-20 px-8 bg-slate-50">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-black text-slate-900 mb-6">
                  ¿Qué son los CAES?
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Los Certificados de Ahorro Energético son un mecanismo que permite a las empresas 
                  de transporte <strong>monetizar sus ahorros de combustible</strong> derivados de una conducción más eficiente.
                </p>
                <ul className="space-y-4">
                  {[
                    "Obligación de las petroleras de comprar ahorros",
                    "Ingresos directos por conducción eficiente",
                    "Sin inversión inicial necesaria"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700">
                      <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-emerald-600 rounded-3xl p-10 text-white">
                <div className="text-6xl font-black mb-4">+50%</div>
                <div className="text-xl font-bold mb-2">Más ingresos con MOBILSAFE</div>
                <p className="text-emerald-100">
                  Frente a gestores que solo tramitan papeles, nosotros mejoramos tu conducción real.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Diferenciación */}
        <section className="py-20 px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-black text-center text-slate-900 mb-4">
              ¿Por qué MOBILSAFE consigue más?
            </h2>
            <p className="text-center text-slate-500 mb-12 max-w-2xl mx-auto">
              No somos un gestor más. Somos expertos en flotas que maximizan tu ahorro real.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { 
                  icon: Users,
                  title: "90 días de acompañamiento",
                  desc: "Formación continua a tus conductores para maximizar el ahorro real, no solo el papel."
                },
                { 
                  icon: Target,
                  title: "Expertos en TRA-020",
                  desc: "La ficha más difícil pero más rentable. Nuestro equipo domina la normativa al detalle."
                },
                { 
                  icon: Zap,
                  title: "Acuerdos exclusivos",
                  desc: "Negociamos directamente con sujetos obligados para conseguir el mejor precio por tus CAES."
                }
              ].map((item, i) => (
                <div key={i} className="p-8 bg-white border border-slate-200 rounded-2xl hover:shadow-xl transition-all">
                  <div className="w-14 h-14 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center mb-5">
                    <item.icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-bold text-slate-800 text-lg mb-3">{item.title}</h3>
                  <p className="text-slate-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Proceso */}
        <section className="py-20 px-8 bg-slate-900">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-black text-center text-white mb-12">
              ¿Cómo funciona?
            </h2>
            
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Calcula", desc: "Estima tu potencial de ingresos CAES" },
                { step: "02", title: "Agenda", desc: "Reunión gratuita para analizar tu caso" },
                { step: "03", title: "Implementa", desc: "Instalamos y formamos a tu equipo" },
                { step: "04", title: "Cobra", desc: "Recibe tus ingresos por ahorro energético" },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="text-5xl font-black text-emerald-500 mb-4">{item.step}</div>
                  <h3 className="font-bold text-white text-lg mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-24 px-8 bg-emerald-600 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          </div>
          <div className="max-w-2xl mx-auto text-center relative">
            <h2 className="text-4xl font-black text-white mb-6">
              ¿15 minutos para descubrir tu potencial?
            </h2>
            <p className="text-xl text-emerald-100 mb-10">
              Sin compromiso. Te explicamos cómo maximizar tus ingresos CAES.
            </p>
            <button className="px-10 py-5 bg-white hover:bg-slate-50 text-emerald-600 rounded-xl font-bold text-lg transition-all hover:shadow-xl">
              QUIERO MI REUNIÓN GRATUITA
            </button>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-8 bg-white border-t border-slate-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-4">
                <img src={MOBILSAFE_LOGO} alt="MOBILSAFE" className="h-6" />
                <span className="text-slate-400 text-sm">© 2026 MOBILSAFE Fleet Technology</span>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <a href="#" className="text-slate-400 hover:text-emerald-600 flex items-center gap-2">
                  <Phone className="w-4 h-4" /> +34 663 837 350
                </a>
                <a href="#" className="text-slate-400 hover:text-emerald-600 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> info@mobilsafe.es
                </a>
              </div>
            </div>
          </div>
        </footer>

        {/* Botón volver */}
        <button
          onClick={() => setVista('selector')}
          className="fixed bottom-6 left-6 px-4 py-3 bg-slate-900 text-white rounded-full shadow-xl flex items-center gap-2 hover:bg-slate-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" /> Volver
        </button>
      </div>
    );
  }

  return null;
};

export default MockupFondoBlancoV2;
